import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  SpaceBetween, Container, Header, Box, Button, Input,
  FormField, Table, StatusIndicator, Spinner, Alert
} from '@cloudscape-design/components';
import ThemeToggle from './ThemeToggle.jsx';
import { apiFetch } from '../lib/apiFetch.js';
import { useAuth } from '../auth/AuthProvider.jsx';

const API_BASE_URL = 'https://css2g9ujye.execute-api.ap-south-1.amazonaws.com';

// Auth: Cognito group membership.
//
// Non-admin users are bounced by ProtectedRoute (adminOnly) before we get
// here, so by the time this component renders we're guaranteed to have an
// authenticated admin. Username comes from the auth context — no more
// harmonyShim retry loop, no more {username}@2026 password.
//
// History (for the curious): the original Phase 1 build had a session-
// scoped password gate (`${username}@2026`) because the app pre-dated
// Cognito. Replaced with the group-claim check on 23 Jun 2026 once Phase
// 2/3 had landed and `admins` group membership was the source of truth.

export default function AdminPage() {
  const navigate = useNavigate();
  const { username } = useAuth();
  const [activeTab, setActiveTab] = useState('feedback');

  const TABS = [
    { id: 'feedback', label: 'Feedback', icon: '💬' },
    { id: 'metrics', label: 'Metrics', icon: '📊' },
  ];

  return (
    <div className="image-studio-page">
      <div className="studio-container">
        <ThemeToggle />
        <Link to="/" className="studio-back-link" aria-label="Back to SmartBiz Toolkit">
          ← Back to SmartBiz Toolkit
        </Link>
        {/* Hero banner */}
        <div className="studio-hero">
          <div className="studio-hero-left">
            <div className="studio-hero-icon">⚙️</div>
            <div className="studio-hero-text">
              <h1 className="studio-hero-title">Admin Panel</h1>
              <p className="studio-hero-subtitle">Feedback, metrics, and tool management</p>
              <div className="studio-hero-status">
                <span className="studio-status-dot"></span>
                <span>Logged in as {username}</span>
              </div>
              {/* The per-page Logout button used to live here (paired with
                  the {username}@2026 password gate). Removed on 23 Jun 2026
                  — Cognito session logout is the only logout, handled
                  globally by useAuth().signOut() (not surfaced on this
                  page; users who want to switch identity sign out from the
                  toolkit shell). */}
            </div>
          </div>
          <div className="studio-hero-ops">
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`studio-op-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="studio-op-icon">{tab.icon}</span>
                <span className="studio-op-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="studio-content">
          <SpaceBetween size="l">
            {activeTab === 'feedback' && <FeedbackPanel />}
            {activeTab === 'metrics' && <MetricsPanel />}
          </SpaceBetween>
        </div>
      </div>
    </div>
  );
}

// ── Feedback Panel ──
function FeedbackPanel() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { fetchFeedbacks(); }, []);

  const fetchFeedbacks = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/admin/feedbacks`);
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const data = await res.json();
      setFeedbacks(data.feedbacks || []);
    } catch (err) {
      setError(err.message || 'Failed to load feedbacks');
    } finally {
      setLoading(false);
    }
  };

  const ratingType = (r) => r >= 4 ? 'success' : r >= 3 ? 'warning' : 'error';

  // Summary stats
  const avgRating = feedbacks.length > 0
    ? (feedbacks.reduce((s, f) => s + (f.rating || 0), 0) / feedbacks.length).toFixed(1)
    : '-';
  const lowRatings = feedbacks.filter(f => f.rating <= 2).length;
  const highRatings = feedbacks.filter(f => f.rating >= 4).length;

  // Per-tool summary
  const toolSummary = {};
  for (const f of feedbacks) {
    const tool = f.tool || 'unknown';
    if (!toolSummary[tool]) toolSummary[tool] = { tool, count: 0, totalRating: 0, comments: 0 };
    toolSummary[tool].count++;
    toolSummary[tool].totalRating += (f.rating || 0);
    if (f.comment && f.comment.trim()) toolSummary[tool].comments++;
  }
  const toolSummaryRows = Object.values(toolSummary)
    .map(t => ({ ...t, avgRating: (t.totalRating / t.count).toFixed(1) }))
    .sort((a, b) => b.count - a.count);

  return (
    <SpaceBetween size="m">
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
        <MetricCard label="Total Feedback" value={feedbacks.length} emoji="💬" />
        <MetricCard label="Avg Rating" value={avgRating} emoji="⭐" />
        <MetricCard label="Positive (4-5⭐)" value={highRatings} emoji="👍" color="#22c55e" />
        <MetricCard label="Negative (1-2⭐)" value={lowRatings} emoji="👎" color={lowRatings > 0 ? '#ef4444' : undefined} />
      </div>

      {/* Per-tool summary table */}
      <Container header={<Header variant="h3">Feedback by Tool</Header>}>
        <Table
          columnDefinitions={[
            { id: 'tool', header: 'Tool', width: 200, cell: (item) => item.tool },
            { id: 'count', header: 'Responses', width: 110, cell: (item) => item.count },
            { id: 'avgRating', header: 'Avg Rating', width: 110, cell: (item) => `⭐ ${item.avgRating}` },
            { id: 'comments', header: 'With Comments', width: 130, cell: (item) => item.comments },
          ]}
          items={toolSummaryRows}
          variant="embedded"
          sortingDisabled
          empty={<Box textAlign="center" padding="s">No data</Box>}
        />
      </Container>

      {/* Full feedback list */}
      <Container header={
        <Header variant="h2" actions={<Button iconName="refresh" onClick={fetchFeedbacks} loading={loading}>Refresh</Button>}>
          All Feedback ({feedbacks.length})
        </Header>
      }>
        {error && <Alert type="warning">{error}</Alert>}
        {loading ? (
          <Box textAlign="center" padding="l"><Spinner size="large" /></Box>
        ) : (
          <Table
            columnDefinitions={[
              { id: 'timestamp', header: 'Date', width: 150, cell: (item) => formatDate(item.timestamp) },
              { id: 'user', header: 'User', width: 110, cell: (item) => item.user || '-' },
              { id: 'tool', header: 'Tool', width: 150, cell: (item) => item.tool || '-' },
              { id: 'category', header: 'Category', width: 130, cell: (item) => item.category || '-' },
              { id: 'rating', header: 'Rating', width: 100, cell: (item) => (
                <StatusIndicator type={ratingType(item.rating)}>
                  {'⭐'.repeat(item.rating || 0)}
                </StatusIndicator>
              )},
              { id: 'comment', header: 'Comment', cell: (item) => item.comment || '-' },
            ]}
            items={feedbacks}
            variant="embedded"
            sortingDisabled
            empty={<Box textAlign="center" padding="l">No feedback yet.</Box>}
          />
        )}
      </Container>
    </SpaceBetween>
  );
}

// ── Metrics Panel ──
function MetricsPanel() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchMetrics(); }, []);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/adoption-stats`);
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const data = await res.json();
      setMetrics(data.tools || []);
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
      setMetrics([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Box textAlign="center" padding="l"><Spinner size="large" /></Box>;
  }

  const totalRuns = (metrics || []).reduce((sum, t) => sum + (t.runs || 0), 0);
  // Backwards-compat: topUsers may be strings or {name, count} objects
  const totalUsers = new Set(
    (metrics || []).flatMap(t => (t.topUsers || []).map(u => typeof u === 'string' ? u : u.name))
  ).size;
  const activeTools = (metrics || []).filter(t => t.status === 'active').length;

  return (
    <SpaceBetween size="m">
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
        <MetricCard label="Total Runs" value={totalRuns} emoji="🚀" />
        <MetricCard label="Unique Users" value={totalUsers} emoji="👥" />
        <MetricCard label="Active Tools" value={activeTools} emoji="🛠️" />
      </div>

      {/* Per-tool breakdown */}
      <Container header={
        <Header variant="h2" actions={<Button iconName="refresh" onClick={fetchMetrics} loading={loading}>Refresh</Button>}>
          Per-Tool Breakdown
        </Header>
      }>
        <Table
          columnDefinitions={[
            { id: 'tool', header: 'Tool', width: 180, cell: (item) => item.tool },
            { id: 'parent', header: 'Category', width: 140, cell: (item) => item.parent },
            { id: 'runs', header: 'Runs', width: 90, cell: (item) => item.runs || 0 },
            { id: 'users', header: 'Users', width: 90, cell: (item) => item.users || 0 },
            { id: 'lastUsed', header: 'Last Used', width: 130, cell: (item) => item.lastUsed || '-' },
            { id: 'topUsers', header: 'Top Users', width: 200, cell: (item) => {
              if (!item.topUsers?.length) return '-';
              // Backwards-compat: API may return plain strings or {name, count} objects
              return item.topUsers
                .map(u => typeof u === 'string' ? u : `${u.name} (${u.count})`)
                .join(', ');
            }},
            { id: 'status', header: 'Status', width: 110, cell: (item) => {
              const s = { active: 'success', 'in-dev': 'in-progress' }[item.status] || 'stopped';
              return <StatusIndicator type={s}>{item.status}</StatusIndicator>;
            }},
          ]}
          items={metrics || []}
          variant="embedded"
          sortingDisabled
        />
      </Container>
    </SpaceBetween>
  );
}

function MetricCard({ label, value, emoji, color }) {
  return (
    <div style={{
      padding: '16px 20px', borderRadius: 10, border: '1px solid var(--color-border-divider-default, #e9ebed)',
      background: 'var(--color-background-container-content, #ffffff)', textAlign: 'center',
    }}>
      <Box fontSize="heading-l">{emoji}</Box>
      <Box fontSize="heading-xl" fontWeight="bold" margin={{ top: 'xxs' }} color={color ? undefined : 'inherit'}>
        <span style={color ? { color } : {}}>{value}</span>
      </Box>
      <Box color="text-body-secondary" fontSize="body-s" margin={{ top: 'xxs' }}>{label}</Box>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) +
      ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}
